// A device/zone address (ip_address, local_ip_address) is stored as a bare host,
// e.g. "192.168.1.50" — no scheme. Passing that straight to window.open/href makes
// the browser resolve it as a path relative to the current origin
// (https://access.panther.com.br/192.168.1.50) instead of opening the device.
// Same fix already existed once, locally, in DeviceProbe.tsx — this centralizes it.
export function ensureHttpScheme(address: string): string {
  const trimmed = address.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `http://${trimmed}`;
}

/**
 * What may be typed into a device or camera address field.
 *
 * " https://torktools.nube.tec.br:9016" reached production because the form stored
 * `e.target.value` verbatim — pasted from a chat message, saved with the leading blank. The space
 * is invisible in the monospace row that displays it, and everything built from the value breaks:
 * the webhook URL, the direct LAN dial the apps make in "direct mode", the snapshot fetch. The
 * device goes on reporting "Online" from its heartbeat while every command sent to it fails,
 * which is the hardest kind of fault to find.
 *
 * ALL whitespace goes, not just the ends: no host, IP, URL or stream path contains a space, so an
 * inner one is a typo mid-edit or two addresses pasted into one field. Stripped as it is typed —
 * like the person-name fields — so the character simply never appears and nobody has to be taught
 * the rule.
 *
 * The backend holds the same line for anything that reaches it another way: condo-manager's
 * `services.NormalizeDeviceAddress` trims the ends and REFUSES an inner space (it cannot guess
 * which half was meant, and a wrong guess points a gate at another machine).
 */
export function sanitizeDeviceAddress(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\s+/g, '');
}
